import React from 'react';
import { motion } from 'framer-motion';
import {
  Users, BookOpen, Eye, CreditCard, TrendingUp,
  Crown, Clock, Trash2, Shield, ShieldOff, BarChart3,
  Link2, Percent, Euro, Plus, Copy, Check as CheckIcon, X,
  Ban, UserCheck, Gift, CalendarPlus, Download, History, MoreVertical, Mail, Send, Tag
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { TabsContent } from '../../ui/tabs';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { StatCard } from '../StatCard';

export const PaymentsTab = ({ ctx }) => {
  const { paymentsData, users } = ctx;
  return (
  <TabsContent value="payments" className="space-y-6" data-testid="payments-content">
              {/* Payment Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Euro} label="Totale Omzet" value={`€${paymentsData?.total_revenue?.toFixed(2) || '0.00'}`} color="green-500" />
                <StatCard icon={CreditCard} label="Totaal Betalingen" value={paymentsData?.total_payments || 0} color="blue-500" />
                <StatCard icon={CheckIcon} label="Betaald" value={paymentsData?.paid_count || 0} color="green-500" />
                <StatCard icon={X} label="Mislukt" value={paymentsData?.failed_count || 0} color="red-500" />
              </div>

              {/* Payments List */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display text-xl flex items-center gap-2">
                    <Euro className="w-5 h-5 text-gold" />
                    Betalingsoverzicht
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {paymentsData?.payments?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-2 font-medium text-muted-foreground">Datum</th>
                            <th className="text-left py-3 px-2 font-medium text-muted-foreground">Gebruiker</th>
                            <th className="text-left py-3 px-2 font-medium text-muted-foreground">Bedrag</th>
                            <th className="text-left py-3 px-2 font-medium text-muted-foreground">Status</th>
                            <th className="text-left py-3 px-2 font-medium text-muted-foreground">Beschrijving</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentsData.payments.map((p, i) => (
                            <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                              <td className="py-3 px-2 text-xs text-muted-foreground">
                                {p.created_at ? new Date(p.created_at).toLocaleDateString('nl-NL', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'}) : '-'}
                              </td>
                              <td className="py-3 px-2">
                                <div className="font-medium">{p.user_name || '-'}</div>
                                <div className="text-xs text-muted-foreground">{p.user_email}</div>
                              </td>
                              <td className="py-3 px-2 font-medium">€{(p.amount || 0).toFixed(2)}</td>
                              <td className="py-3 px-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  p.status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                  p.status === 'open' || p.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                }`}>
                                  {p.status === 'paid' ? 'Betaald' : p.status === 'open' ? 'Open' : p.status === 'pending' ? 'In afwachting' : p.status === 'failed' ? 'Mislukt' : p.status === 'canceled' ? 'Geannuleerd' : p.status}
                                </span>
                              </td>
                              <td className="py-3 px-2 text-xs text-muted-foreground">{p.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="font-display text-lg">Nog geen betalingen</p>
                      <p className="text-sm mt-1">Betalingen verschijnen hier zodra gebruikers een abonnement afsluiten via Mollie</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Active Subscriptions */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display text-xl flex items-center gap-2">
                    <Crown className="w-5 h-5 text-gold" />
                    Actieve Abonnementen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {users.filter(u => u.subscription?.status === 'active').length > 0 ? (
                    <div className="space-y-3">
                      {users.filter(u => u.subscription?.status === 'active').map((u, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                          <div>
                            <p className="font-medium">{u.name || u.email}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {u.subscription?.payment_method === 'sepa_directdebit' ? 'SEPA Autoincasso' : 'Handmatig'}
                              {u.subscription?.subscription_end_date && ` · Verloopt ${new Date(u.subscription.subscription_end_date).toLocaleDateString('nl-NL')}`}
                            </p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-destructive border-destructive/20 hover:bg-destructive/10"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/admin/users/${u.id}/cancel-mollie`, {}, { headers: { Authorization: `Bearer ${token}` }});
                                toast.success('Abonnement opgezegd');
                                fetchData();
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Opzeggen mislukt');
                              }
                            }}
                            data-testid={`cancel-sub-${u.id}`}
                          >
                            Opzeggen
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-6 text-muted-foreground text-sm">Geen actieve abonnementen</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
  );
};
